'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { COLOR_MODE_STORAGE_KEY, type ColorMode } from './color-mode';

interface ColorModeContextValue {
  colorMode: ColorMode;
  setColorMode: (mode: ColorMode) => void;
  toggleColorMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null);

function getDocumentColorMode(): ColorMode {
  if (typeof document === 'undefined') {
    return 'dark';
  }
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

function applyColorMode(mode: ColorMode) {
  document.documentElement.classList.toggle('dark', mode === 'dark');
  document.documentElement.dataset.colorMode = mode;
}

function persistColorMode(mode: ColorMode) {
  try {
    window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
  } catch (error) {
    console.warn('Unable to persist color mode preference:', error);
  }
}

export function ColorModeProvider({ children }: { children: ReactNode }) {
  const [colorMode, setColorModeState] = useState<ColorMode>('dark');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const documentMode = getDocumentColorMode();

      setColorModeState(documentMode);
      applyColorMode(documentMode);
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  const setColorMode = useCallback((mode: ColorMode) => {
    setColorModeState(mode);
    persistColorMode(mode);
    applyColorMode(mode);
  }, []);

  const toggleColorMode = useCallback(() => {
    setColorMode(colorMode === 'dark' ? 'light' : 'dark');
  }, [colorMode, setColorMode]);

  const value = useMemo(
    () => ({
      colorMode,
      setColorMode,
      toggleColorMode,
    }),
    [colorMode, setColorMode, toggleColorMode]
  );

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
}

export function useColorMode() {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error('useColorMode must be used inside ColorModeProvider.');
  }
  return context;
}
